import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ChevronLeft, ChevronRight, Loader2, InfoIcon, LayoutGrid, RectangleHorizontal } from 'lucide-react';
import { useZoom } from './AlertSnapshotViewer/hooks/useZoom';
import {
  MonitorAlertFullDto,
  MonitorAlertEventDto,
  QuillAnalysisMetadata,
  QuillEscalationMetadata,
  statusConfig,
} from '../Alert/types';
import { fetchAlert } from '@/lib/api/monitoring/alert';
import { AlertPreviewDto, MonitorAlertsSearched } from '@/app/AlertsDashboard/types';
import { useSite } from '@/hooks/siteHooks';
import { ProjectFullDto } from '@/app/Sites/siteApi'; // Import ProjectFullDto
import { StatusBadge } from '@/components/ui/status-badge';
import { Comparator, cn } from '@/lib/utils';
import axiosClient from '@/lib/auth';
import { EnumAlertStatus } from '@/lib/api/types.gen';
export const findAlertByReadableId = async (
  readableId: string
): Promise<AlertPreviewDto | null> => {
  const params = {
    PageNumber: 1,
    PageSize: 1, // We only expect one result (or the first one)
    'Filter.Operator': 'And',
    'Filter.FilterGroups[0][operator]': 'And',
    'Filter.FilterGroups[0][filters][0][field]': 'alertId',
    'Filter.FilterGroups[0][filters][0][action]': Comparator.ILike,
    'Filter.FilterGroups[0][filters][0][value]': readableId.replace(/^([aA])[-\s]?(\d+)$/, '$2'),
  };

  try {
    const response = await axiosClient.get<MonitorAlertsSearched>('/AlertMonitoring/search', {
      params,
    });

    if (response.data && response.data.rows && response.data.rows.length > 0) {
      return response.data.rows[0]; // Return the first matching alert
    }
    return null; // No alert found
  } catch (error) {
    console.error(`Error fetching alert by readableId ${readableId}:`, error);
    return null;
  }
};

interface AuditItem {
  id: string; // from note.id
  timestamp: Date; // from note.date (which is a Date object)
  type: 'escalation' | 'analysis';
  originalStatus?: EnumAlertStatus; // from metadata.previousStatus
  newStatus?: EnumAlertStatus; // from metadata.newStatus
  resultingStatus?: EnumAlertStatus; // from metadata.status
  notes: string; // from note.note
  event: MonitorAlertEventDto; // The corresponding event
  imageUrl: string;
}

export function AlertAuditPage() {
  const [alertIdInput, setAlertIdInput] = useState<string>('');
  const [searchInputAlertId, setSearchInputAlertId] = useState<string | null>(null); // User's input ID
  const [guidToFetch, setGuidToFetch] = useState<string | null>(null); // GUID obtained from searchInputAlertId
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const alertInputRef = useRef<HTMLInputElement>(null);

  // alertData, projectData, isLoading, error will come from useQuery hooks for the main alert fetch
  const [auditItems, setAuditItems] = useState<AuditItem[]>([]);
  const [currentAuditItemIndex, setCurrentAuditItemIndex] = useState<number>(0);
  const [isAiPrimerOpen, setIsAiPrimerOpen] = useState<boolean>(false);
  const [showGrid, setShowGrid] = useState<boolean>(false);
  const [showDetections, setShowDetections] = useState<boolean>(true);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const { zoomLevel, panPosition, handleMouseDown, resetZoom, selectionBox } = useZoom({
    containerRef: imageContainerRef,
    imageRef,
  });

  const alertIdFromUrl = searchParams.get('alertId');

  useEffect(() => {
    if (alertIdFromUrl) {
      setGuidToFetch(null);
      setAuditItems([]);
      setSearchError(null);
      setAlertIdInput(alertIdFromUrl);
      setSearchInputAlertId(alertIdFromUrl);
    }
  }, [alertIdFromUrl]);

  // Add event listener for Escape key to focus the input
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        alertInputRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (auditItems && auditItems.length > 0) {
      auditItems.forEach((item) => {
        if (item.imageUrl) {
          const img = new Image();
          img.src = item.imageUrl;
          // No need to handle img.onload or img.onerror for preloading
        }
      });
    }
  }, [auditItems]);

  const {
    data: foundAlertPreview,
    isLoading: isSearchingGuid,
    error: findGuidError,
    isFetching: isFetchingGuid,
  } = useQuery<AlertPreviewDto | null, Error>({
    queryKey: ['findAlertGuid', searchInputAlertId],
    queryFn: () => {
      if (!searchInputAlertId) return Promise.resolve(null);
      return findAlertByReadableId(searchInputAlertId);
    },
    enabled: !!searchInputAlertId,
    retry: 1,
  });

  useEffect(() => {
    setSearchError(null); // Reset search error on new search attempt or data change

    if (isSearchingGuid || isFetchingGuid) {
      // If we are actively searching or fetching, don't draw conclusions yet
      return;
    }

    // Use the destructured variables from the useQuery hook directly:
    if (findGuidError) {
      // findGuidError is the 'error' object from useQuery
      setSearchError(`Error finding alert: ${findGuidError.message}`);
      setGuidToFetch(null);
    } else if (foundAlertPreview) {
      // foundAlertPreview is the 'data' from useQuery (AlertPreviewDto | null)
      if (foundAlertPreview.id) {
        setGuidToFetch(foundAlertPreview.id);
      } else {
        // This case should ideally not happen if the API guarantees an id for found alerts
        setSearchError(
          `Alert found by readable ID '${searchInputAlertId}', but it's missing a GUID.`
        );
        setGuidToFetch(null);
      }
    } else if (searchInputAlertId) {
      // If searchInputAlertId is set, and we're not loading (isSearchingGuid/isFetchingGuid handled above),
      // and no error (findGuidError handled above), and no data (foundAlertPreview is null/undefined),
      // it implies 'not found'.
      setSearchError(`No alert found with ID: ${searchInputAlertId}`);
      setGuidToFetch(null);
    }
  }, [foundAlertPreview, isSearchingGuid, isFetchingGuid, findGuidError, searchInputAlertId]);

  const {
    data: alertData,
    isLoading: isAlertLoading,
    error: alertError,
    isFetching: isAlertFetching,
  } = useQuery<MonitorAlertFullDto, Error>({
    queryKey: ['alertAudit', guidToFetch], // Use guidToFetch now
    queryFn: () => {
      if (!guidToFetch) {
        return Promise.reject(new Error('Internal: GUID to fetch is missing'));
      }
      return fetchAlert(guidToFetch);
    },
    enabled: !!guidToFetch, // Enabled only when we have a GUID
    retry: 1,
  });

  // Storing projectData in a state variable to persist across alert fetches if projectId is the same
  const [projectData, setProjectData] = useState<ProjectFullDto | null>(null);
  const {
    data: fetchedProjectData,
    isLoading: isProjectLoading,
    error: projectError,
    isFetching: isProjectFetching,
  } = useSite({
    siteId: alertData?.projectId || '',
    companyId: alertData?.companyId || '',
    enabled:
      !!alertData?.projectId && !!alertData?.companyId && projectData?.id !== alertData?.projectId, // Only fetch if projectData is null or for a different project
  });

  useEffect(() => {
    if (fetchedProjectData) {
      setProjectData(fetchedProjectData);
    }
  }, [fetchedProjectData]);

  // Clear project data if alert data is cleared or has no project ID
  useEffect(() => {
    if (!alertData || !alertData.projectId) {
      setProjectData(null);
    }
  }, [alertData]);

  useEffect(() => {
    setAuditItems([]); // Clear previous items when alertData changes
    setCurrentAuditItemIndex(0);

    if (alertData?.recentNotes && alertData?.events) {
      const processed: AuditItem[] = [];
      const notesToProcess = alertData.recentNotes.filter(
        (note) =>
          note.metadata &&
          (note.metadata.type === 'QuillEscalation' || note.metadata.type === 'QuillAnalysis')
      );

      for (const note of notesToProcess) {
        if (!note.id || !note.date || !note.metadata) continue;

        const metadata = note.metadata as QuillEscalationMetadata | QuillAnalysisMetadata;
        const correspondingEvent = alertData.events.find(
          (event) => event.id === metadata.monitoringEventId
        );

        if (correspondingEvent) {
          if (metadata.type === 'QuillEscalation') {
            processed.push({
              id: note.id,
              timestamp: new Date(note.date), // Ensure it's a Date object
              type: 'escalation',
              originalStatus: metadata.previousStatus,
              newStatus: metadata.newStatus,
              notes: note.metadata.type === 'QuillEscalation' ? note.note || '' : '',
              event: correspondingEvent,
              imageUrl:
                correspondingEvent.values?.find((v) => v.type === 'SnapshotImage')?.sourceUrl || '',
            });
          } else if (metadata.type === 'QuillAnalysis') {
            processed.push({
              id: note.id,
              timestamp: new Date(note.date), // Ensure it's a Date object
              type: 'analysis',
              resultingStatus: metadata.status,
              notes: note.metadata.type === 'QuillAnalysis' ? note.metadata.description : '',
              event: correspondingEvent,
              imageUrl:
                correspondingEvent.values?.find((v) => v.type === 'SnapshotImage')?.sourceUrl || '',
            });
          }
        }
      }

      processed.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      setAuditItems(processed);
    }
  }, [alertData]);

  // Combined loading and error states
  const isLoading =
    isSearchingGuid ||
    isFetchingGuid ||
    isAlertLoading ||
    isAlertFetching ||
    isProjectLoading ||
    isProjectFetching;
  const error =
    searchError ||
    (findGuidError ? findGuidError.message : null) ||
    (alertError ? alertError.message : null) ||
    (projectError ? projectError.message : null);

  function handleLoadAlert() {
    if (alertIdInput.trim()) {
      setGuidToFetch(null); // Reset guid in case of new search, this will disable the main alert fetch query
      // alertData will clear automatically when guidToFetch is null and the query re-evaluates
      setProjectData(null); // Clear previous project data
      setAuditItems([]); // Clear previous audit items
      setSearchError(null); // Clear previous search errors
      setCurrentAuditItemIndex(0); // Reset to first item
      setImageDimensions({ width: 0, height: 0, offsetX: 0, offsetY: 0 }); // Reset image dimensions
      // Force refetch even if same ID by clearing first then setting
      setSearchInputAlertId(null);
      setTimeout(() => {
        setSearchInputAlertId(alertIdInput.trim());
      }, 0);
    }
  }

  function handleNext() {
    if (currentAuditItemIndex < auditItems.length - 1) {
      setCurrentAuditItemIndex((prev) => prev + 1);
    } else {
      // Loop back to start
      setCurrentAuditItemIndex(0);
    }
  }

  function handleBack() {
    if (currentAuditItemIndex > 0) {
      setCurrentAuditItemIndex((prev) => prev - 1);
    } else {
      // Loop to end
      setCurrentAuditItemIndex(auditItems.length - 1);
    }
  }

  const currentAuditItem = useMemo(() => {
    return auditItems.length > 0 ? auditItems[currentAuditItemIndex] : null;
  }, [auditItems, currentAuditItemIndex]);

  // Parse polygon string into array of points
  const parsePolygon = useCallback((polygonStr: string): { x: number; y: number }[] => {
    try {
      const numbers = polygonStr.match(/[-+]?[0-9]*\.?[0-9]+/g);
      if (!numbers) return [];
      const points: { x: number; y: number }[] = [];
      for (let i = 0; i < numbers.length; i += 2) {
        const x = parseFloat(numbers[i]);
        const y = parseFloat(numbers[i + 1]);
        if (!isNaN(x) && !isNaN(y)) {
          points.push({ x, y });
        }
      }
      return points;
    } catch (e) {
      console.error('Error parsing polygon:', e);
      return [];
    }
  }, []);

  // Get bounding boxes from current event
  const currentBoundingBoxes = useMemo(() => {
    return (
      currentAuditItem?.event?.values
        ?.filter((v) => v.type === 'PersonSeen' || v.type === 'VehicleSeen')
        .map((v) => ({
          type: v.type as string,
          coordinates: parsePolygon(v.txtValue || ''),
        })) ?? []
    );
  }, [currentAuditItem, parsePolygon]);

  // Calculate image dimensions
  const calculateImageDimensions = useCallback(() => {
    const img = imageRef.current;
    const container = imageContainerRef.current;
    if (!container || !img || !img.naturalWidth) return;

    const containerRect = container.getBoundingClientRect();
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerRect.width / containerRect.height;

    let renderedWidth, renderedHeight, offsetX, offsetY;

    if (imgAspect > containerAspect) {
      // Image is wider - fit to width
      renderedWidth = containerRect.width;
      renderedHeight = containerRect.width / imgAspect;
      offsetX = 0;
      offsetY = (containerRect.height - renderedHeight) / 2;
    } else {
      // Image is taller - fit to height
      renderedHeight = containerRect.height;
      renderedWidth = containerRect.height * imgAspect;
      offsetX = (containerRect.width - renderedWidth) / 2;
      offsetY = 0;
    }

    setImageDimensions({ width: renderedWidth, height: renderedHeight, offsetX, offsetY });
  }, []);

  // Handle image load to get dimensions
  const handleImageLoad = useCallback(() => {
    calculateImageDimensions();
  }, [calculateImageDimensions]);

  // Recalculate dimensions and reset zoom when audit item changes (for cached images)
  useEffect(() => {
    // Reset dimensions first, then recalculate after a small delay for cached images
    setImageDimensions({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
    resetZoom();
    const timer = setTimeout(() => {
      calculateImageDimensions();
    }, 50);
    return () => clearTimeout(timer);
  }, [currentAuditItemIndex, calculateImageDimensions, resetZoom]);

  return (
    <div className=" h-[calc(100vh-3.5rem)] grid grid-cols-[auto_320px] items-start bg-[#111] text-white overflow-hidden">
      <section className="h-full flex flex-col overflow-hidden">
        {!alertData && !isLoading && !error && (
          <div className="flex-1 flex items-center justify-center min-h-0">
            <div className="text-center text-gray-400">
              <div className="mb-4">
                <svg className="mx-auto h-16 w-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-300 mb-2">No Alert Loaded</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Enter an Alert ID in the panel on the right to load an alert for review.
              </p>
            </div>
          </div>
        )}

        {/* Toolbar - always visible when not in empty state */}
        {(alertData || isLoading || error) && (
          <div className='flex justify-between w-full border-b '>
            {alertData && auditItems.length > 1 ? (
              <div className="flex items-center justify-center ">
                <Button variant="ghost" onClick={handleBack} className='rounded-none ' hotkey={{ keyboard: 'arrowleft'}} >

                </Button>

                <span className="text-sm text-gray-400 min-w-28 font-semibold border rounded-full px-2 py-1 text-center">
                  Item {currentAuditItemIndex + 1} / {auditItems.length}
                </span>
       
                <Button
                variant="ghost"
                  onClick={handleNext}
                  className='rounded-none '
                  hotkey={{ keyboard: 'arrowright'}}
                >
                </Button>
              </div>
            ) : (
              <div className="flex-1" />
            )}
            <div className="flex gap-1 p-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={showGrid ? 'default' : 'outline'}
                      hotkey={{ keyboard: 'arrowup'}}
        
                      onClick={() => setShowGrid(!showGrid)}
                      className={showGrid ? 'bg-lime text-black hover:bg-lime/80' : ''}
                      disabled={!alertData}
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{showGrid ? 'Hide' : 'Show'} Activation Zone</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={showDetections ? 'default' : 'outline'}
                      hotkey={{ keyboard: 'arrowdown'}}
                 
                      onClick={() => setShowDetections(!showDetections)}
                      className={showDetections ? 'bg-lime text-black hover:bg-lime/80' : ''}
                      disabled={!alertData}
                    >
                      <RectangleHorizontal className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{showDetections ? 'Hide' : 'Show'} Detections</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}

        {/* Image viewer area */}
        <div
          ref={imageContainerRef}
          className={cn(
            'flex-1 flex items-center justify-center min-h-0 relative overflow-hidden touch-none',
            zoomLevel > 1 ? 'cursor-zoom-out' : 'cursor-zoom-in'
          )}
          onMouseDown={handleMouseDown}
        >
          {error && (
            <div className="p-8 text-center text-red-400">Error: {error}</div>
          )}

          {isLoading && !error && (
            <div className="text-center text-gray-400">
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-lime mb-4" />
              <p>Loading alert data...</p>
            </div>
          )}

          {alertData && currentAuditItem?.imageUrl ? (
                <>
                  <img
                    ref={imageRef}
                    src={currentAuditItem.imageUrl}
                    alt={`Snapshot for audit step ${currentAuditItemIndex + 1}`}
                    className="pointer-events-none h-full w-full select-none object-contain shadow-lg transition-transform duration-100"
                    onLoad={handleImageLoad}
                    style={{
                      transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                      transformOrigin: '0 0',
                    }}
                  />
                  {/* Activation Zone Overlay */}
                  {showGrid && currentAuditItem.event?.activationZone && imageDimensions.width > 0 && (
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{
                        transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                        transformOrigin: '0 0',
                      }}
                    >
                    <div
                      className="absolute"
                      style={{
                        left: imageDimensions.offsetX,
                        top: imageDimensions.offsetY,
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                      }}
                    >
                      <svg className="absolute left-0 top-0 h-full w-full">
                        {(() => {
                          const coordsMatch = currentAuditItem.event.activationZone.match(/\(\((.*?)\)\)/);
                          if (!coordsMatch) return null;
                          const points = parsePolygon(coordsMatch[1]);
                          if (points.length === 0) return null;
                          const scaledPoints = points.map((point) => ({
                            x: point.x * imageDimensions.width,
                            y: point.y * imageDimensions.height,
                          }));
                          const pathData =
                            scaledPoints
                              .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
                              .join(' ') + ' Z';
                          return (
                            <>
                              <defs>
                                <mask id="activationZoneMaskAudit">
                                  <rect width="100%" height="100%" fill="white" />
                                  <path d={pathData} fill="black" />
                                </mask>
                              </defs>
                              <rect
                                width="100%"
                                height="100%"
                                fill="rgba(0, 0, 0, 0.4)"
                                mask="url(#activationZoneMaskAudit)"
                              />
                              <path
                                d={pathData}
                                fill="none"
                                stroke="rgba(255, 255, 255, 0.7)"
                                strokeWidth={2}
                                vectorEffect="non-scaling-stroke"
                              />
                            </>
                          );
                        })()}
                      </svg>
                    </div>
                    </div>
                  )}
                  {/* Bounding Box Overlay */}
                  {showDetections && imageDimensions.width > 0 && (
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{
                        transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                        transformOrigin: '0 0',
                      }}
                    >
                    <div
                      className="absolute"
                      style={{
                        left: imageDimensions.offsetX,
                        top: imageDimensions.offsetY,
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                      }}
                    >
                      {currentBoundingBoxes.map((box, index) => {
                        if (box.coordinates.length < 2) return null;
                        const minX = Math.min(...box.coordinates.map((c) => c.x));
                        const maxX = Math.max(...box.coordinates.map((c) => c.x));
                        const minY = Math.min(...box.coordinates.map((c) => c.y));
                        const maxY = Math.max(...box.coordinates.map((c) => c.y));
                        return (
                          <div
                            key={index}
                            className={`absolute border-2 ${
                              box.type === 'PersonSeen'
                                ? 'border-[#FF6666] bg-[#FF6666]/10'
                                : 'border-[#66B2FF] bg-[#66B2FF]/10'
                            }`}
                            style={{
                              left: `${minX * 100}%`,
                              top: `${minY * 100}%`,
                              width: `${(maxX - minX) * 100}%`,
                              height: `${(maxY - minY) * 100}%`,
                            }}
                          />
                        );
                      })}
                    </div>
                    </div>
                  )}
                  {/* Selection box overlay for drag-to-zoom */}
                  {selectionBox && (
                    <div
                      className="pointer-events-none absolute border-2 border-white/50 bg-white/10"
                      style={{
                        left: selectionBox.x + (imageRef.current?.getBoundingClientRect().left ?? 0) - (imageContainerRef.current?.getBoundingClientRect().left ?? 0),
                        top: selectionBox.y + (imageRef.current?.getBoundingClientRect().top ?? 0) - (imageContainerRef.current?.getBoundingClientRect().top ?? 0),
                        width: selectionBox.width,
                        height: selectionBox.height,
                      }}
                    />
                  )}
                </>
          ) : alertData && !isLoading && !error ? (
            <div className="text-center text-gray-500">
              <div className="mb-4">
                <svg className="mx-auto h-16 w-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p>No image available for this audit item</p>
            </div>
          ) : null}
        </div>
      </section>

      <section className="border-l  h-[calc(100vh-3.5rem)] overflow-y-scroll">
        <div data-onboarding="alert-audit-form">
          <div className='border-b flex justify-between items-center'>
            <h1 className='p-4 h-[56px] font-semibold'>Quill Audit Tool</h1>
            <div className='w-[56px] h-[56px] border-l flex items-center justify-center'>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className='h-5 w-5 text-lime cursor-help flex items-center justify-center' />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Press <kbd>Escape</kbd> to refocus on the input</p>
                    <p>Press <kbd>Enter</kbd> to load the results</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div className="p-4 flex flex-col gap-2 border-b">
            <p className='text-sm'>Enter an Alert ID to load an alert for review.</p>
            <Input
              ref={alertInputRef}
              type="text"
              value={alertIdInput}
              onChange={(e) => setAlertIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading && alertIdInput.trim()) {
                  handleLoadAlert();
                }
              }}
              placeholder="Enter Alert ID"
              className="flex-grow border-gray-600 bg-gray-800 text-white placeholder-gray-500 focus:border-red-500 focus:ring-red-500"
            />
            <Button
              onClick={handleLoadAlert}
              disabled={isLoading || !alertIdInput.trim()} // isLoading now covers all loading states
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading...
                </>
              ) : (
                'Load Alert'
              )}
            </Button>
          </div>
        </div>
         

          {alertData && ( // Project data is used for AI primer, page can render most things without it initially
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div>
                {/* Left Column: Audit Step Details & No Items Message */}
                <div className="">
                  <div className="p-4 border-b">
                  <h2 className="font-semibold">Results for Alert {alertData.alertId}</h2>
                  <p className='text-xs text-gray-300'> 
                            Item {currentAuditItemIndex + 1} of {auditItems.length}
                          
                         </p>
                  </div>
                  {auditItems.length === 0 && !isLoading && searchInputAlertId && !error && (
                    <div className="mb-4 rounded-md border border-gray-700 bg-[#222] p-4">
                      <p className="text-center text-gray-500">
                        No relevant audit items (quill-escalation or quill-analysis notes) found for
                        this alert.
                      </p>
                    </div>
                  )}
                
                </div>

                {/* Right Column: AI Primer */}
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="quill-instructions">
                    <AccordionTrigger className="px-4 py-3 font-semibold hover:no-underline">
                      Quill Instructions
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      {isProjectLoading || isProjectFetching ? (
                        <p className="text-xs text-gray-500">Loading AI Primer...</p>
                      ) : projectError ? (
                        <p className="text-xs text-red-400">
                          Error loading AI Primer: {projectError.message}
                        </p>
                      ) : projectData && projectData.aiPrimer ? (
                        <div className="prose text-sm dark:prose-invert max-w-none whitespace-pre-wrap break-words text-white/90">
                          {projectData.aiPrimer}
                        </div>
                      ) : projectData && !projectData.aiPrimer ? (
                        <p className="text-xs text-gray-500">
                          No AI Primer content available for this project.
                        </p>
                      ) : alertData && alertData.projectId ? (
                        <p className="text-xs text-gray-500">
                          AI Primer not found for project ID: {alertData.projectId}.
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">
                          No project associated with this alert to load AI Primer from.
                        </p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

            
            </div>
          )}
          
          {auditItems.length > 0 && currentAuditItem && (
            <div className="flex flex-col border-t border-b">
              <div className="flex justify-between items-center p-4 pb-2">
                <h2 className="font-semibold">Quill Results</h2>
            
              </div>
              {currentAuditItem && (
                    <div className=" mx-4 border rounded-md grid bg-[#1F3632]/20">
                      {/* Audit Type and Status Changes */}
                      {currentAuditItem.type === 'escalation' ? (
                        <div className="flex flex-col">
                               <span className="font-semibold text-sm p-2 border-b w-full">Escalation from:</span>
                        <div className="flex items-center gap-2">
                   
                          <div className="flex items-center gap-1 p-2">
                            <StatusBadge
                            className='text-xs'
                              status={currentAuditItem.originalStatus || 'N/A'}
                              color={statusConfig[currentAuditItem.originalStatus as EnumAlertStatus]?.color}
                            />
                            <ChevronRight className="h-4 w-4 text-gray-500" />
                            <StatusBadge
                                className='text-xs'
                              status={currentAuditItem.newStatus || 'N/A'}
                              color={statusConfig[currentAuditItem.newStatus as EnumAlertStatus]?.color}
                            />
                          </div>
                        </div></div>
                      ) : (
                        <>
                        <span className="font-semibold  text-sm p-2 w-full border-b">Analysis</span>
                        <div className="flex items-center gap-2 p-2">
                     
                          <span className="text-sm text-gray-300">Resulting Status:</span>
                          <StatusBadge
                            className='text-xs'
                            status={currentAuditItem.resultingStatus || 'N/A'}
                            color={statusConfig[currentAuditItem.resultingStatus as EnumAlertStatus]?.color}
                          />
                        </div>
                        </>
                      )}
                      
                      {/* Timestamp */}
                      <p className="text-xs text-gray-400 p-2 border-t">
                        Occurred {currentAuditItem.timestamp.toLocaleDateString()} at{' '}
                        {currentAuditItem.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                 
                  )}
              
              <div className="p-4">
            
                <div className="text-sm leading-relaxed">
                  <h3 className='font-semibold'>Notes:</h3><p className='text-gray-300'>
                  {currentAuditItem.notes}</p>
                </div>
              </div>
              
           
            </div>
          )}
          </section>
          
   
    </div>
  );
}

export default AlertAuditPage;
